import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuthSession, logoutAuthSession, startGoogleLogin, type AuthUser } from '../../lib/auth';
import { getWalletSummary } from '../../lib/api';
import type { WalletSummaryResponse } from '../../lib/types';
import UiIcon from '../common/UiIcon';

const PANEL_WIDTH = 'min(100vw, 376px)';
const RAIL_WIDTH = 64;
const MOBILE_RAIL_WIDTH = 56;

const HOME_BACK_ROUTES = new Set([
  '/history',
  '/wallet/recharge',
  '/internal-review-login',
  '/origins',
  '/manifesto',
  '/archives',
  '/credits',
  '/legal',
  '/terms',
  '/privacy',
  '/refund',
  '/contact',
  '/business-info',
]);

function getFloatingBackTarget(pathname: string): string | null {
  if (pathname === '/' || pathname === '/spreads') {
    return null;
  }

  if (pathname === '/themes' || pathname.startsWith('/themes/')) {
    return '/spreads';
  }

  if (HOME_BACK_ROUTES.has(pathname)) {
    return '/';
  }

  return '/';
}

function getInitial(user: AuthUser | null): string {
  const source = user?.name?.trim() || user?.email?.trim() || '묘';
  return source.slice(0, 1).toUpperCase();
}

function MenuGlyph({ isOpen }: { isOpen: boolean }) {
  if (isOpen) {
    return <UiIcon name="close" className="h-5 w-5" />;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-[5px]" aria-hidden="true">
      <span className="block h-[2px] w-5 rounded-full bg-current" />
      <span className="block h-[2px] w-3.5 rounded-full bg-current" />
      <span className="block h-[2px] w-5 rounded-full bg-current" />
    </div>
  );
}

function AccountMark({ user }: { user: AuthUser | null }) {
  const [failedPicture, setFailedPicture] = useState(false);

  useEffect(() => {
    setFailedPicture(false);
  }, [user?.picture]);

  if (user?.picture && !failedPicture) {
    return (
      <img
        src={user.picture}
        alt=""
        className="h-11 w-11 rounded-full border border-[#8f6a43]/50 object-cover"
        referrerPolicy="no-referrer"
        onError={() => setFailedPicture(true)}
      />
    );
  }

  if (user) {
    return (
      <div className="grid h-11 w-11 place-items-center rounded-full border border-[#9b7448]/60 bg-[#2a1d16] text-base font-extrabold text-[#f6d7a0]">
        {getInitial(user)}
      </div>
    );
  }

  return (
    <div className="grid h-11 w-11 place-items-center rounded-full border border-[#8f6a43]/50 bg-[#241912] text-[#d5a95f]">
      <UiIcon name="cat" className="h-5 w-5" />
    </div>
  );
}

function RailButton({
  label,
  icon,
  onClick,
  active = false,
}: {
  label: string;
  icon: React.ComponentProps<typeof UiIcon>['name'] | 'menu';
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`group grid h-11 w-11 place-items-center border transition-colors duration-200 ${
        active
          ? 'border-[#c59550]/70 bg-[#2d1f17] text-[#f6d7a0]'
          : 'border-[#6d5137]/50 bg-[#1a120f] text-[#caa16a] hover:border-[#b98545]/70 hover:bg-[#241811] hover:text-[#f0cf93]'
      }`}
    >
      {icon === 'menu' ? <MenuGlyph isOpen={active} /> : <UiIcon name={icon} className="h-5 w-5" />}
    </button>
  );
}

function MenuItem({
  label,
  hint,
  icon,
  onClick,
  tone = 'default',
  disabled = false,
}: {
  label: string;
  hint: string;
  icon: React.ComponentProps<typeof UiIcon>['name'];
  onClick: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-[#673631]/60 bg-[#231412] text-[#f0c1ba] hover:border-[#8b4740] hover:bg-[#2b1715]'
      : 'border-[#6b4a2f]/60 bg-[#1a120f] text-[#f2dec0] hover:border-[#9b7448]/70 hover:bg-[#241811]';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group flex w-full items-center gap-3 border px-3.5 py-3 text-left transition-colors duration-200 ${toneClass} disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center border border-[#7c5a38]/60 bg-[#120c0a] text-current">
        <UiIcon name={icon} className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-5">{label}</span>
        <span className="mt-0.5 block truncate text-[11px] leading-5 text-[#cbb08c]/78">{hint}</span>
      </span>
      <UiIcon name="arrow-right" className="h-3.5 w-3.5 shrink-0 text-[#96724b] transition-colors group-hover:text-[#d3a762]" />
    </button>
  );
}

export default function GlobalSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [wallet, setWallet] = useState<WalletSummaryResponse | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isLoadingWallet, setIsLoadingWallet] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    let disposed = false;

    const loadSession = async () => {
      setIsLoadingSession(true);
      const session = await getAuthSession();
      if (!disposed) {
        setUser(session?.user ?? null);
        setIsLoadingSession(false);
      }

      if (!session?.user) {
        if (!disposed) {
          setWallet(null);
          setIsLoadingWallet(false);
        }
        return;
      }

      setIsLoadingWallet(true);
      const walletSummary = await getWalletSummary();
      if (!disposed) {
        setWallet(walletSummary);
        setIsLoadingWallet(false);
      }
    };

    void loadSession();
    return () => {
      disposed = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleGoogleLogin = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    startGoogleLogin(returnTo);
  };

  const handleLogout = async () => {
    if (isBusy) return;

    setIsBusy(true);
    const ok = await logoutAuthSession();
    setIsBusy(false);

    if (ok) {
      setUser(null);
      setIsOpen(false);
      navigate('/');
      return;
    }

    alert('로그아웃에 실패했습니다. 잠시 후 다시 시도해 주세요.');
  };

  const floatingBackTarget = getFloatingBackTarget(location.pathname);
  const showBackButton = Boolean(floatingBackTarget);
  const railRight = isOpen ? PANEL_WIDTH : '0px';
  const panelTransform = isOpen ? 'translateX(0)' : 'translateX(100%)';
  const balanceLabel = isLoadingWallet ? '확인 중' : `${wallet?.balancePoints ?? 0} 츄르`;

  const navigateAndClose = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleFloatingBack = () => {
    if (!floatingBackTarget) return;
    navigateAndClose(floatingBackTarget);
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[110] bg-black/50 backdrop-blur-[2px] transition-opacity duration-300 ${
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setIsOpen(false)}
      />

      <nav
        aria-label="묘안 빠른 메뉴"
        className="fixed bottom-0 top-0 z-[140] flex w-14 flex-col items-center border-l border-[#644829]/50 bg-[#120d0b]/96 px-1.5 py-4 text-[#d7ad70] shadow-[-12px_0_28px_rgba(0,0,0,0.28)] transition-[right] duration-300 ease-out md:w-16 md:px-2"
        style={{
          right: railRight,
          width: `clamp(${MOBILE_RAIL_WIDTH}px, 4vw, ${RAIL_WIDTH}px)`,
        }}
      >
        <RailButton
          label={isOpen ? '사이드바 닫기' : '사이드바 열기'}
          icon="menu"
          active={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        />

        {showBackButton && (
          <div className="mt-3">
            <RailButton label="뒤로가기" icon="arrow-left" onClick={handleFloatingBack} />
          </div>
        )}

        <div className="my-5 h-px w-8 bg-[#5b412a]/75" />

        <RailButton label="메인으로 이동" icon="home" onClick={() => navigateAndClose('/')} />
        <div className="mt-3">
          <RailButton label="지난 리딩 보기" icon="history" onClick={() => (user ? navigateAndClose('/history') : setIsOpen(true))} />
        </div>

        <div className="mt-auto grid gap-3">
          <RailButton label="츄르 충전" icon="coin" onClick={() => (user ? navigateAndClose('/wallet/recharge') : setIsOpen(true))} />
        </div>
      </nav>

      <aside
        aria-label="묘안 타로 사이드바"
        className="fixed bottom-0 right-0 top-0 z-[130] border-l border-[#6b4a25]/50 bg-[#15100d] text-[#f4e2c3] shadow-[-18px_0_38px_rgba(0,0,0,0.36)] transition-transform duration-300 ease-out"
        style={{ width: PANEL_WIDTH, transform: panelTransform }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex h-full flex-col">
          <header className="border-b border-[#6e5238]/40 px-6 pb-5 pt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#c79b66]">Myoan Tarot</p>
                <h2 className="mt-3 font-serif text-2xl font-extrabold tracking-[-0.03em] text-[#fff1d8]">오늘의 세션</h2>
                <p className="mt-2 text-xs leading-5 text-[#cdb493]/80">계정, 지갑, 지난 리딩을 한곳에서 정리합니다.</p>
              </div>
              <button
                type="button"
                aria-label="사이드바 닫기"
                onClick={() => setIsOpen(false)}
                className="grid h-10 w-10 shrink-0 place-items-center border border-[#6d5137]/60 bg-[#1f1713] text-[#d7ad70] transition-colors hover:border-[#b98545]/70 hover:text-[#f6d7a0]"
              >
                <UiIcon name="close" className="h-5 w-5" />
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <section className="border border-[#6e5238]/40 bg-[#1a120f] p-4">
              <div className="flex items-center gap-3">
                <AccountMark user={isLoadingSession ? null : user} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#f7ead5]">
                    {isLoadingSession ? '계정 상태 확인 중' : user ? user.name || '묘안 방문자' : '로그인이 필요합니다'}
                  </p>
                  <p className="mt-1 truncate text-xs text-[#c9aa84]/80">
                    {isLoadingSession ? '잠시만 기다려 주세요' : user?.email || '결제 결과와 지난 리딩을 보관하려면 로그인하세요'}
                  </p>
                </div>
              </div>

              {!isLoadingSession && !user && (
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="mt-4 flex w-full items-center justify-center gap-2 border border-[#a86a36]/60 bg-[#8c381d] px-4 py-3 text-sm font-semibold text-[#fff3e3] transition-colors hover:bg-[#9f4323]"
                >
                  Google 로그인
                  <UiIcon name="arrow-right" className="h-4 w-4" />
                </button>
              )}
            </section>

            {user && (
              <section className="mt-4 flex items-center justify-between gap-3 border border-[#6e5238]/40 bg-[#1a120f] px-4 py-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#b98c5a]">Churu Wallet</p>
                  <p className="mt-1 text-lg font-extrabold text-[#f4d292]">{balanceLabel}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigateAndClose('/wallet/recharge')}
                  className="shrink-0 border border-[#a77a38]/70 bg-[#241811] px-3 py-2 text-xs font-bold text-[#efd096] transition-colors hover:border-[#d0a152] hover:bg-[#2d1d13]"
                >
                  충전하기
                </button>
              </section>
            )}

            <section className="mt-5">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.26em] text-[#a7815c]">Navigation</p>
              <div className="grid gap-2">
                <MenuItem
                  label="새 리딩 시작"
                  hint="스프레드 선택으로 이동"
                  icon="sparkles"
                  onClick={() => navigateAndClose('/spreads')}
                />
                <MenuItem
                  label="지난 리딩 보기"
                  hint={user ? '내 보관함 열기' : '로그인 후 이용 가능'}
                  icon="history"
                  onClick={() => (user ? navigateAndClose('/history') : handleGoogleLogin())}
                />
                <MenuItem
                  label="츄르 충전"
                  hint={user ? '지갑 잔액 채우기' : '로그인 후 충전 가능'}
                  icon="coin"
                  onClick={() => (user ? navigateAndClose('/wallet/recharge') : handleGoogleLogin())}
                />
                <MenuItem label="서비스 안내" hint="묘안 소개와 이용 안내" icon="book" onClick={() => navigateAndClose('/origins')} />
              </div>
            </section>

            <section className="mt-5">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.26em] text-[#a7815c]">Account</p>
              <div className="grid gap-2">
                {user?.isAdmin && (
                  <MenuItem label="관리자 콘솔" hint="운영 현황 확인" icon="compass" onClick={() => navigateAndClose('/admin')} />
                )}
                {user ? (
                  <MenuItem
                    label={isBusy ? '로그아웃 중...' : '로그아웃'}
                    hint="현재 계정에서 나가기"
                    icon="arrow-left"
                    onClick={() => {
                      void handleLogout();
                    }}
                    tone="danger"
                    disabled={isBusy}
                  />
                ) : (
                  <MenuItem label="Google 로그인" hint="계정 연결하기" icon="user" onClick={handleGoogleLogin} />
                )}
              </div>
            </section>
          </div>
        </div>
      </aside>
    </>
  );
}
